import { IText, util, Object as FabricObject } from 'fabric';

// --- Store original prototype methods ---
const RENDER_FN_BACKUP = '__original_render_liquid';
const RENDER_TEXT_FN_BACKUP = '__original_render_text_liquid';

if (!(IText.prototype as any)[RENDER_FN_BACKUP]) {
    (IText.prototype as any)[RENDER_FN_BACKUP] = (IText.prototype as any)._render;
}
if (!(IText.prototype as any)[RENDER_TEXT_FN_BACKUP]) {
    (IText.prototype as any)[RENDER_TEXT_FN_BACKUP] = (IText.prototype as any)._renderText;
}

const originalRender = (IText.prototype as any)[RENDER_FN_BACKUP];

type FabricObjectWithLiquidEffect = IText & {
    isLiquidWarped?: boolean;
    _render_original_liquid?: (ctx: CanvasRenderingContext2D) => void;
    liquidParams?: any;
    isLiquidEffectEnabled?: boolean;
};

// --- Default Parameters ---
const defaultLiquidParams = {
    radius: 100,
    flowStrength: 0.1, // This is the effective value after scaling
    noiseStrength: 10,
    noiseFrequency: 0.02,
    angle: 0,
};

// --- Main Effect Functions ---

export function enableLiquidWarp(textObject: IText, options: any = {}) {
    if (!textObject) return;
    const obj = textObject as FabricObjectWithLiquidEffect;

    // --- Always update parameters on every call ---
    const existingParams = obj.liquidParams || {};
    // If flowStrength is provided directly, use it. Otherwise fall back to rotation/defaults.
    // We expect options.flowStrength to be the direct value (e.g. 0.12).
    
    let flowStrength = options.flowStrength;
    if (flowStrength === undefined) {
         // Backwards compatibility or default logic
         const flowStrengthRaw = options.rotations ?? (existingParams.flowStrengthRaw !== undefined ? existingParams.flowStrengthRaw : 30);
         flowStrength = flowStrengthRaw * 0.003;
    }

    obj.liquidParams = {
        radius: options.radius ?? existingParams.radius ?? defaultLiquidParams.radius,
        flowStrength: flowStrength, 
        flowStrengthRaw: undefined, // No longer strictly needed if we pass direct value
        angle: options.rotationOffset ?? existingParams.angle ?? defaultLiquidParams.angle,
        noiseStrength: options.noiseStrength ?? existingParams.noiseStrength ?? defaultLiquidParams.noiseStrength,
        noiseFrequency: options.noiseFrequency ?? existingParams.noiseFrequency ?? defaultLiquidParams.noiseFrequency,
    };
    obj.isLiquidWarped = true;

    // --- Set up the _render override only once ---
    if (!obj.isLiquidEffectEnabled) {
        obj.isLiquidWarped = true;
        obj.isLiquidEffectEnabled = true;

        obj._render = function(ctx: CanvasRenderingContext2D) {
            if (!this.isLiquidWarped) {
                return originalRender.call(this, ctx);
            }
            
            // 1. Hijack _renderText and call parent _render to set up context
            const _renderTextOriginal = this._renderText;
            this._renderText = () => {};
            FabricObject.prototype._render.call(this, ctx);
            this._renderText = _renderTextOriginal;

            // 2. Define rendering parameters for anti-aliasing
            const resolutionScale = 2.0; // Increase to 2x or 3x for better quality
            const padding = 60; // Increase padding to avoid clipping effects at edges
            const logicalWidth = this.width + padding * 2;
            const logicalHeight = this.height + padding * 2;
            const superWidth = Math.ceil(logicalWidth * resolutionScale);
            const superHeight = Math.ceil(logicalHeight * resolutionScale);

            if (this.width <= 0 || this.height <= 0) return;

            // 3. Create high-resolution temp canvas
            const tempCanvas = util.createCanvasElement();
            tempCanvas.width = superWidth;
            tempCanvas.height = superHeight;
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) return;

            // 4. Render upscaled text onto the temp canvas
            tempCtx.translate(superWidth / 2, superHeight / 2);
            tempCtx.scale(resolutionScale, resolutionScale);
            _renderTextOriginal.call(this, tempCtx);

            // 5. Apply the liquify effect on the high-res image data
            const srcImageData = tempCtx.getImageData(0, 0, superWidth, superHeight);
            const dstImageData = tempCtx.createImageData(superWidth, superHeight);
            applyLiquidEffect(srcImageData, dstImageData, this.liquidParams);
            
            // 6. Put the distorted result back onto the temp canvas
            tempCtx.setTransform(1, 0, 0, 1, 0, 0);
            tempCtx.putImageData(dstImageData, 0, 0);

            // 7. Draw the final, downscaled image for a smooth result
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(tempCanvas, -logicalWidth / 2, -logicalHeight / 2, logicalWidth, logicalHeight);
        };
    }

    // --- Trigger a re-render ---
    obj.dirty = true;
    obj.canvas?.requestRenderAll();
}

export function disableLiquidWarp(textObject: IText) {
    const obj = textObject as FabricObjectWithLiquidEffect;
    if (obj && obj.isLiquidEffectEnabled) {
        delete obj.isLiquidEffectEnabled;
        obj.isLiquidWarped = false;
        delete obj.liquidParams;
        obj._render = originalRender;
        obj.dirty = true;
        obj.canvas?.requestRenderAll();
    }
}


// --- Pixel Manipulation Logic ---

function applyLiquidEffect(src: ImageData, dst: ImageData, params: any) {
    const W = src.width;
    const H = src.height;

    for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
            const { dx, dy } = getDisplacement(x, y, W, H, params);
            const sx = x - dx;
            const sy = y - dy;

            const rgba = sampleBilinear(src, sx, sy);

            const k = (y * W + x) * 4;
            dst.data[k] = rgba[0];
            dst.data[k + 1] = rgba[1];
            dst.data[k + 2] = rgba[2];
            dst.data[k + 3] = rgba[3];
        }
    }
}

function getDisplacement(x: number, y: number, W: number, H: number, params: any) {
    const cx = W / 2;
    const cy = H / 2;

    const dx_vec = x - cx;
    const dy_vec = y - cy;
    const r = Math.sqrt(dx_vec * dx_vec + dy_vec * dy_vec);

    // Scale radius with canvas size for a more consistent effect
    const radius = params.radius * (W / (W - 80));
    const k = liquidKernel(r, radius);

    const flowX = -dy_vec;
    const flowY = dx_vec;

    const angleRad = util.degreesToRadians(params.angle);
    const cosA = Math.cos(angleRad);
    const sinA = Math.sin(angleRad);

    const rotatedFlowX = flowX * cosA - flowY * sinA;
    const rotatedFlowY = flowX * sinA + flowY * cosA;

    const noise = (Math.sin(x * params.noiseFrequency) + Math.cos(y * params.noiseFrequency * 0.9)) * 0.5;

    return {
        dx: rotatedFlowX * k * params.flowStrength + noise * k * params.noiseStrength,
        dy: rotatedFlowY * k * params.flowStrength + noise * k * params.noiseStrength,
    };
}

function liquidKernel(r: number, R: number) {
    if (r > R) return 0;
    const x = r / R;
    return (1 - x * x) ** 2;
}

function sampleBilinear(img: ImageData, x: number, y: number): number[] {
    const w = img.width;
    const h = img.height;
    x = Math.max(0, Math.min(x, w - 1));
    y = Math.max(0, Math.min(y, h - 1));

    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const x1 = Math.min(x0 + 1, w - 1);
    const y1 = Math.min(y0 + 1, h - 1);

    const dx_fract = x - x0;
    const dy_fract = y - y0;

    const i = (yy: number, xx: number) => {
        const j = (yy * w + xx) * 4;
        return [img.data[j], img.data[j + 1], img.data[j + 2], img.data[j + 3]];
    };

    const c00 = i(y0, x0);
    const c10 = i(y0, x1);
    const c01 = i(y1, x0);
    const c11 = i(y1, x1);

    const out = [0, 0, 0, 0];
    for (let c = 0; c < 4; c++) {
        const v0 = c00[c] * (1 - dx_fract) + c10[c] * dx_fract;
        const v1 = c01[c] * (1 - dx_fract) + c11[c] * dx_fract;
        out[c] = v0 * (1 - dy_fract) + v1 * dy_fract;
    }
    return out;
}